import { Injector } from '@angular/core';
import { BulkSection } from './BulkSection';
import { ActionCache } from '../Data/BulkData/ActionCache';
import { CodeName } from '../../service/lookup-service/code-name.model';
import { AuthorisedProduct } from '../Data/Art57Data/AuthorisedProduct';

export class BulkUpdateSection extends BulkSection {
  actionCache: ActionCache = {
    tuplesJson: null,
    actionOption: [],
    actionEnabled: false,
  };
  request = 'bulkupdaterequests';
  criteriaModel: any;
  placeholderRequest: any =
    'Search for the product set title or status of the requests. Type any character and press [ENTER] to execute the search.';
  repeatersToTransform = [
    'drugingredients',
    'actions',
    'pharmaceuticalforms',
    'atccodes',
    'indicationmeddracodes',
    'organisations',
    'anynames',
    'fullnames',
    'shortnames',
    'genericnames',
    'companynames',
    'evcodes',
  ];

  nonRepeatersToTransform = [
    'qppvs',
    'mfls',
    'ownerorganisations',
    'authorisationcountrycodes',
    'authorisationprocedures',
    'authorisationstatuses',
    'previews',
  ];

  otherFieldsToTransform = [
    'versionToExport',
    'assessment',
    'art57Format',
    'interimFormat',
    'startDate',
    'endDate',
    'authorisedDevelopment',
  ];

  initialModel = {
    versionToExport: { filterStatus: true, filterData: 6 },
    drugingredients: { filterStatus: false, filterData: [[{}]] },
    actionset: { action: [{ componentID: 'pending_init' }] },
    pharmaceuticalforms: { filterStatus: false, filterData: [[{}]] },
    atccodes: { filterStatus: false, filterData: [[{}]] },
    indicationmeddracodes: { filterStatus: false, filterData: [[{}]] },
    organisations: { filterStatus: false, filterData: [[{}]] },
    anynames: { filterStatus: false, filterData: [[{}]] },
    fullnames: { filterStatus: false, filterData: [[{}]] },
    shortnames: { filterStatus: false, filterData: [[{}]] },
    genericnames: { filterStatus: false, filterData: [[{}]] },
    companynames: { filterStatus: false, filterData: [[{}]] },
    evcodes: { filterStatus: false, filterData: [[{}]] },
  };

  isLoadingActionSet: boolean = false;
  actionSetLoaded: boolean = false;
  actionSetFilename: string = '';
  errors: any = undefined;
  actionsCheckedRepeater: string[] = [];

  constructor(injector: Injector) {
    super(injector);
    this.criteriaModel = structuredClone(this.initialModel);
  }

  // clear cache
  public clearActionCache(): void {
    this.actionCache = {
      tuplesJson: null,
      actionOption: [],
      actionEnabled: false,
    };
  }

  public extractActionOption(): void {
    const result: CodeName[] = [];

    // Always normalize the node into a list of child objects
    function getChildren(node: any): any[] {
      if (!node) return [];

      // If the node is already an array, return its elements
      if (Array.isArray(node)) return node;

      // If the node is an object with a "children" property, return its children
      if (node.children && typeof node.children === 'object') {
        return Object.values(node.children);
      }

      return [];
    }


    // The node is included if ANY of its direct children has terminal = true
    function hasTerminalChild(node: any): boolean {
      const children = getChildren(node);
      return children.some((child) => child && child.terminal === true);
    }

    /**
     * Traverse the tree carrying an array of ancestor descriptions.
     * - `ancestors` contains the breadcrumb of parent node descriptions (left to right).
     * - We DO NOT include the root 'authorisedproducts' in results, and by default we do not add its desc to ancestors.
     *   If you want to include the root's desc in the breadcrumb, pass `[String(root.desc ?? '')].filter(Boolean)`
     *   as initial `ancestors` instead of [].
     */
    function traverse(node: any, ancestors: string[]): void {
      if (!node) return;

      // If the node is an array, iterate through its elements
      if (Array.isArray(node)) {
        for (const sub of node) {
          traverse(sub, ancestors);
        }
        return;
      }

      // Build the current node's display name as:
      // parent1.desc / parent2.desc / ... / current.desc
      const currentDesc = String(node?.desc ?? '').trim();
      const breadcrumbParts = [
        ...ancestors.filter(Boolean), // only non-empty
        currentDesc || '', // include current desc (may be empty, we clean later)
      ].filter(Boolean);

      const breadcrumb = breadcrumbParts.join(' / ');

      // Apply rule: include the node if a direct child is terminal
      // OR it's a group (but not the root 'authorisedproducts')
      if (
        (hasTerminalChild(node) ||
        (node.nodeType === 'group' && node.xpath !== 'authorisedproducts')) && (node.ignoreSection == undefined || node.ignoreSection === false) ) 
      {
        result.push({
          code: String(node.xpath),
          name: breadcrumb, // <-- concatenated breadcrumb name
        });
      }

      // Recursively traverse the children
      // For descendants, push this node's desc into ancestors if it is not the root
      // We avoid adding the root 'authorisedproducts' to the breadcrumb by checking its path.
      const isRoot = node?.xpath === 'authorisedproducts';
      const nextAncestors = isRoot
        ? ancestors // do not include root's desc by default
        : [...ancestors, currentDesc].filter(Boolean);

      const children = getChildren(node);
      for (const child of children) {
        traverse(child, nextAncestors);
      }
    }

    // Start traversal from authorisedproducts
    const root = this.actionCache.tuplesJson.authorisedproducts.children.authorisedproduct;

    // If you want the root desc to appear at the very left of all breadcrumbs, replace [] with:
    // [String(root?.desc ?? '')].filter(Boolean)
    traverse(root, []);

    // Sort alphabetically by `name` ignoring case
    this.actionCache.actionOption = result.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
  }

  public override clearRequestsCache(): void {
    super.clearRequestsCache();
    this.actionSetFilename = '';
  }

  override resetSection(): void {
    super.resetSection();
    this.actionsCheckedRepeater = [];
    this.clearActionCache();
  }
}
